import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { isIosNativeAppRuntime } from './runtimeCapabilities';
import { appleAccountRestoreRequired, connectApplePurchase } from './applePurchaseSession';

export const IOS_STOREKIT_PRODUCT_IDS = {
  dayPass: 'com.ziawellnav.ios.daypass',
  monthly: 'com.ziawellnav.app.pro.monthly',
  yearly: 'com.ziawellnav.app.pro.yearly',
} as const;

export type IosStoreKitPlan = keyof typeof IOS_STOREKIT_PRODUCT_IDS;
export type IosStoreKitProductId = typeof IOS_STOREKIT_PRODUCT_IDS[IosStoreKitPlan];

export interface IosStoreKitProduct {
  id: string;
  displayName: string;
  description: string;
  displayPrice: string;
  price?: number;
  currencyCode?: string;
}

export interface IosStoreKitTransaction {
  id: string;
  originalId: string;
  productId: string;
  purchaseDate: string;
  expiresDate?: string;
  signedTransaction?: string;
}

export interface IosStoreKitEntitlementResult {
  active: boolean;
  cancelled?: boolean;
  pending?: boolean;
  productId?: string;
  latestTransactionId?: string;
  expiresDate?: string;
  transactions: IosStoreKitTransaction[];
}

interface ZiaStoreKitPlugin {
  getProducts(options: { productIds: string[] }): Promise<{ products: IosStoreKitProduct[] }>;
  purchase(options: { productId: string }): Promise<IosStoreKitEntitlementResult>;
  restorePurchases(): Promise<IosStoreKitEntitlementResult>;
  getEntitlements(): Promise<IosStoreKitEntitlementResult>;
  addListener(
    eventName: 'entitlementsChanged',
    listenerFunc: (event: IosStoreKitEntitlementResult) => void,
  ): Promise<PluginListenerHandle>;
}

const ZiaStoreKit = registerPlugin<ZiaStoreKitPlugin>('ZiaStoreKit');

function unavailableResult(): IosStoreKitEntitlementResult {
  return { active: false, transactions: [] };
}

function isStoreKitRuntime(): boolean {
  return isIosNativeAppRuntime();
}

export function productIdForIosPlan(plan: IosStoreKitPlan): IosStoreKitProductId {
  return IOS_STOREKIT_PRODUCT_IDS[plan];
}

export function planForIosProductId(productId?: string): IosStoreKitPlan | null {
  if (productId === IOS_STOREKIT_PRODUCT_IDS.dayPass) return 'dayPass';
  if (productId === IOS_STOREKIT_PRODUCT_IDS.monthly) return 'monthly';
  if (productId === IOS_STOREKIT_PRODUCT_IDS.yearly) return 'yearly';
  return null;
}

export const iosStoreKitService = {
  productIds: Object.values(IOS_STOREKIT_PRODUCT_IDS),

  isAvailable(): boolean {
    return isStoreKitRuntime();
  },

  async getProducts(): Promise<IosStoreKitProduct[]> {
    if (!isStoreKitRuntime()) return [];
    const result = await ZiaStoreKit.getProducts({ productIds: this.productIds });
    return result.products || [];
  },

  async purchase(plan: IosStoreKitPlan): Promise<IosStoreKitEntitlementResult> {
    if (!isStoreKitRuntime()) return unavailableResult();
    const result = await ZiaStoreKit.purchase({ productId: productIdForIosPlan(plan) });
    await connectApplePurchase(result, true);
    return result;
  },

  async restorePurchases(): Promise<IosStoreKitEntitlementResult> {
    if (!isStoreKitRuntime()) return unavailableResult();
    const result = await ZiaStoreKit.restorePurchases();
    await connectApplePurchase(result, true);
    return result;
  },

  async getEntitlements(): Promise<IosStoreKitEntitlementResult> {
    if (!isStoreKitRuntime()) return unavailableResult();
    if (appleAccountRestoreRequired()) return unavailableResult();
    return ZiaStoreKit.getEntitlements();
  },

  /**
   * Fires when the native plugin observes an out-of-band StoreKit transaction
   * (Ask to Buy approval, renewal, purchase on another device, revocation).
   * No-op outside the native iOS runtime.
   */
  async addEntitlementsChangedListener(
    listener: (event: IosStoreKitEntitlementResult) => void,
  ): Promise<PluginListenerHandle | null> {
    if (!isStoreKitRuntime()) return null;
    return ZiaStoreKit.addListener('entitlementsChanged', listener);
  },
};
